import { useState, useEffect, useCallback, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../../components/TanTable';
import ProDetailSlideOver from '../../../components/pro/ProDetailSlideOver';
import { useToast } from '../../../contexts/ToastContext';
import schedulingService from '../../../services/scheduling.service';
import { SchedulingHistoryRecord } from '../../../types/scheduling';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export const HISTORY_RESULT_LIMIT = 50000;

const toISODate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Default the window to today only: with the row cap, a single day can already
// exceed the limit, so a wider default would silently drop rows.
const defaultFromDate = (): string => toISODate(new Date());

const formatDateTime = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString();
};

const getActionTypeColor = (actionType: string): string => {
  switch (actionType) {
    case 'CREATE':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'UPDATE':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'SEND_TO_LC':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    case 'FINALIZE':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300';
    case 'RETURN_TO_SCHEDULER':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
    // CMS-sourced target-date events: set vs cleared read as opposites.
    case 'TARGET_DATE_SET':
      return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300';
    case 'TARGET_DATE_CLEARED':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  }
};

// Rows come from two logs (see SchedulingHistoryRecord.source). CMS target-date
// edits made on the board land in both, so the badge is what tells them apart.
const SOURCE_LABELS: Record<string, string> = {
  scheduling: 'Scheduling',
  cms: 'CMS',
};

const getSourceColor = (source: string): string =>
  source === 'cms'
    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300'
    : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200';

const inputClass =
  'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed';
const labelClass = 'block text-sm font-medium text-label mb-1';

// Read-access to the history search is granted per page, so a user who reaches
// this from the LC/Scheduler board can hold the board page without the history
// endpoints. Say so plainly instead of showing an empty table.
const FORBIDDEN_MESSAGE = "You don't have access to scheduling history. Ask an admin to grant it.";

const errorStatus = (error: any): number | undefined =>
  error?.response?.status ?? error?.status;

interface SchedulingHistoryContentProps {
  /** Prefills the Pro # filter, scoping the initial search to one pro. */
  initialPronumber?: number;
  /** Extra classes for the scroll container (the drawer and the page differ). */
  className?: string;
}

/**
 * Filter bar + results table for scheduling history. Shared by the standalone
 * page (`/scheduling/history`) and the drawer on the LC/Scheduler boards, so the
 * filter rules live in exactly one place. Searches once on mount.
 */
export default function SchedulingHistoryContent({
  initialPronumber,
  className = 'p-4 space-y-4',
}: SchedulingHistoryContentProps) {
  const { showToast } = useToast();
  const [pronumber, setPronumber] = useState<string>(
    initialPronumber != null ? String(initialPronumber) : ''
  );
  const [changedBy, setChangedBy] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>(defaultFromDate());
  const [toDate, setToDate] = useState<string>(toISODate(new Date()));

  const [history, setHistory] = useState<SchedulingHistoryRecord[]>([]);
  const [users, setUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  // Pro opened from a row's Pro # link. The drawer portals to body, so it sits
  // above this content whether it renders as the page or inside the LC drawer.
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  const proFilterActive = pronumber.trim() !== '';

  const runSearch = useCallback(async () => {
    const params: {
      changed_by?: string;
      pronumber?: number;
      start?: string;
      end?: string;
      limit: number;
    } = { limit: HISTORY_RESULT_LIMIT };

    if (changedBy) params.changed_by = changedBy;

    const pro = pronumber.trim();
    if (pro) {
      const proNum = parseInt(pro, 10);
      if (isNaN(proNum)) {
        showToast({ message: 'Please enter a valid pro number', type: 'error' });
        return;
      }
      params.pronumber = proNum;
    } else {
      // Date window only applies when not scoped to a single pro.
      if (fromDate) params.start = fromDate;
      if (toDate) params.end = toDate;
    }

    setLoading(true);
    try {
      const data = await schedulingService.searchHistory(params);
      setHistory(data);
      if (data.length === 0) {
        showToast({ message: 'No history found for these filters', type: 'info' });
      }
    } catch (error: any) {
      const message =
        errorStatus(error) === 403
          ? FORBIDDEN_MESSAGE
          : error.message || 'Failed to fetch history';
      showToast({ message, type: 'error' });
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [changedBy, pronumber, fromDate, toDate, showToast]);

  // Load the user list once, and run the initial search on mount.
  useEffect(() => {
    schedulingService
      .getHistoryUsers()
      .then(setUsers)
      .catch(() => {
        /* dropdown just falls back to "All Users" */
      });
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') runSearch();
  };

  const columns: ColumnDef<SchedulingHistoryRecord>[] = useMemo(
    () => [
      {
        accessorKey: 'pronumber',
        header: 'Pro #',
        size: 120,
        // Keeps the raw value on the accessor so sort/filter still work on the
        // number; only the display becomes a link into the pro drawer.
        cell: ({ row }) => {
          const pro = Number(row.original.pronumber);
          if (!Number.isFinite(pro) || pro <= 0) return row.original.pronumber ?? '-';
          return (
            <button
              onClick={() => setSelectedPro(pro)}
              className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
              title={`Open PRO ${pro}`}
            >
              {pro}
            </button>
          );
        },
      },
      {
        accessorKey: 'changed_at',
        header: 'Date/Time',
        size: 160,
        cell: ({ row }) => formatDateTime(row.original.changed_at),
      },
      {
        accessorKey: 'action_type',
        header: 'Action',
        size: 140,
        cell: ({ row }) => (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${getActionTypeColor(row.original.action_type)}`}
          >
            {row.original.action_type.replace(/_/g, ' ')}
          </span>
        ),
      },
      {
        accessorKey: 'field_name',
        header: 'Field',
        size: 150,
      },
      {
        accessorKey: 'old_value',
        header: 'Old Value',
        size: 150,
        cell: ({ row }) => row.original.old_value || '-',
      },
      {
        accessorKey: 'new_value',
        header: 'New Value',
        size: 150,
        cell: ({ row }) => row.original.new_value || '-',
      },
      {
        accessorKey: 'changed_by',
        header: 'Changed By',
        size: 200,
      },
      {
        accessorKey: 'source',
        header: 'Source',
        size: 110,
        cell: ({ row }) => (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${getSourceColor(row.original.source)}`}
          >
            {SOURCE_LABELS[row.original.source] ?? row.original.source}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className={className}>
      {/* Filter bar */}
      <div className="bg-panel rounded-lg shadow p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="sched-history-pro" className={labelClass}>
              Pro #
            </label>
            <input
              id="sched-history-pro"
              type="text"
              value={pronumber}
              onChange={(e) => setPronumber(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Any"
              className={`${inputClass} w-36`}
            />
          </div>

          <div>
            <label htmlFor="sched-history-user" className={labelClass}>
              Changed By
            </label>
            <select
              id="sched-history-user"
              value={changedBy}
              onChange={(e) => setChangedBy(e.target.value)}
              className={`${inputClass} min-w-[12rem]`}
            >
              <option value="">All Users</option>
              {users.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="sched-history-from" className={labelClass}>
              From
            </label>
            <input
              id="sched-history-from"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              disabled={proFilterActive}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor="sched-history-to" className={labelClass}>
              To
            </label>
            <input
              id="sched-history-to"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              disabled={proFilterActive}
              className={inputClass}
            />
          </div>

          <button
            onClick={runSearch}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
            Apply
          </button>
        </div>

        {proFilterActive && (
          <p className="text-xs text-muted mt-2">
            Date range is ignored when a Pro # is set — showing that pro's full history.
          </p>
        )}
      </div>

      {/* Truncation notice */}
      {history.length >= HISTORY_RESULT_LIMIT && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-4 py-2 text-sm text-amber-800 dark:text-amber-300">
          Showing the most recent {HISTORY_RESULT_LIMIT.toLocaleString()} changes. Narrow the date
          range or filter by user/pro to see more.
        </div>
      )}

      {/* Results Table */}
      <TanTable
        data={history}
        columns={columns}
        loading={loading}
        cardTitle="Scheduling History"
        // id is unique per source only — CMS recnos and scheduling_history ids
        // overlap, so the key has to carry both.
        getRowId={(row) => `${row.source}-${row.id}`}
      />

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={selectedPro !== null}
        onClose={() => setSelectedPro(null)}
      />
    </div>
  );
}
